import React, { ReactElement } from 'react'
import { StylesConfig } from 'react-select'
import loadable from '@loadable/component'
import { Input } from '@/components/atoms/Input/Input'
import { FieldTypes } from '@/components/molecules/EventForm/types'
import { SelectProps } from '@/components/atoms/Select/types'
import { HubspotFormFieldTypes } from '@/components/molecules/HubspotForm/types'
import { Checkbox } from '@/components/atoms/Checkbox/Checkbox'
import { RichTextField } from '@/components/organisms/RichTextField/RichTextField'
import { InputProps } from '@/components/atoms/Input/types'
import { CheckboxProps } from '@/components/atoms/Checkbox/types'

const BaseSelect = loadable(() => import('react-select'))

const Select = loadable(() => import('@/components/atoms/Select/Select'), {
  resolveComponent: (component) => component.Select
})

const customStyles: StylesConfig = {
  control: (provided, state) => ({
    display: 'flex',
    justifyContent: 'space-between',
    height: '64px',
    paddingLeft: '14px',
    fontSize: '18px',
    marginBottom: '32px',
    position: 'relative',
    borderColor: '#E8E8E8',
    border: '1px solid #E8E8E8',
    outline: state.isFocused ? '2px solid #6676D6' : 'none',
    borderRadius: '0.25rem',
    '&:hover': {
      borderColor: '#6676D6'
    }
  }),
  singleValue: (provided) => ({
    ...provided,
    overflow: 'visible'
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    backgroundColor: state.isSelected ? '#6676D6' : provided.backgroundColor,
    '&:hover': {
      backgroundColor: state.isSelected ? '#6676D6' : provided.backgroundColor
    }
  }),

  indicatorSeparator: () => ({
    display: 'none'
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    marginRight: '20px'
  })
}

export function renderFormField({
  component,
  field,
  value,
  onChange
}: {
  component: 'component_select' | 'component_input' | 'component_checkbox'
  field: FieldTypes | HubspotFormFieldTypes
  value: string | { label: string; value: string } | boolean | null
  onChange: (name: string, newValue: unknown) => void
}): ReactElement<InputProps> | ReactElement<SelectProps> | ReactElement<CheckboxProps> | null {
  if (component === 'component_input')
    return (
      <Input
        id={field.name}
        name={field.name}
        value={value as string}
        onChange={(e) => onChange(field.name, e.target.value)}
        required={field.required}
        placeholder={field.placeholder}
        className="rounded border-1 !border-grey-100 w-full text-bodyL !text-text-dark-600 mb-32 placeholder:text-text-dark-600"
        type={field.name === 'email' ? 'email' : 'text'}
      />
    )
  if (component === 'component_select') {
    const options = field.options?.map((opt) => ({ value: opt.text, label: opt.text }))
    return (
      <Select
        id={field.name}
        name={field.name}
        // @ts-ignore
        SelectComponent={BaseSelect}
        options={options}
        onChange={(selected: unknown) => onChange(field.name, selected)}
        styles={customStyles}
        value={value as null | { value: string; label: string }}
        required={field.required}
        placeholder={field.placeholder || ''}
      />
    )
  }
  if (component === 'component_checkbox') {
    return (
      <div className="flex items-center mb-32">
        <Checkbox
          id={field.name}
          name={field.name}
          className="mr-16 !border-grey-100"
          checked={Boolean(value)}
          onChange={(e) => onChange(field.name, e.target.checked)}
          required={field.required}
        />
        {field.description && (
          // eslint-disable-next-line jsx-a11y/label-has-associated-control
          <label htmlFor={field.name}>
            <RichTextField data={field.description} />
          </label>
        )}
      </div>
    )
  }
  return null
}
